import { resolveTitleExpression } from "./search-expression/title.js";
import {
  FISHYMAP_POINT_ICON_SCALE_DEFAULT,
  createEmptySnapshot,
} from "./map-bridge-contract.js";
import {
  normalizeRestoreView,
  normalizeSelectionHistoryBehavior,
  normalizeStatePatch,
} from "./map-bridge-input.js";
import {
  DEFAULT_LAYER_CLIP_MASKS,
  DEFAULT_ENABLED_LAYER_IDS,
} from "./map-signal-contract.js";
import { buildLayerSearchEffects } from "./map-layer-search-effects.js";
import { resolveFishingContestExpression } from "./search-expression/fishing-contest.js";

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function normalizeCatalogRevision(value) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError("map runtime catalogRevision must be a non-negative safe integer");
  }
  return value;
}

function normalizeBookmarkSelectedIds(bookmarks, selectedIds) {
  const allowedIds = new Set(bookmarks.map((bookmark) => bookmark.id));
  const normalizedSelectedIds =
    normalizeStatePatch({
      ui: { bookmarkSelectedIds: selectedIds },
    }).ui?.bookmarkSelectedIds || [];
  return normalizedSelectedIds.filter((bookmarkId) => allowedIds.has(bookmarkId));
}

function normalizeBridgedFilters(signals, searchExpression) {
  const bridged = isPlainObject(signals?._map_bridged?.filters) ? signals._map_bridged.filters : {};
  const layerIdsVisible = Array.isArray(bridged.layerIdsVisible)
    ? bridged.layerIdsVisible
    : cloneJson(DEFAULT_ENABLED_LAYER_IDS);
  const layerClipMasks = hasOwn(bridged, "layerClipMasks")
    ? bridged.layerClipMasks
    : cloneJson(DEFAULT_LAYER_CLIP_MASKS);
  return normalizeStatePatch({
    filters: {
      searchExpression,
      layerIdsVisible,
      layerIdsOrdered: bridged.layerIdsOrdered,
      layerFilterBindingIdsDisabledByLayer:
        bridged.layerFilterBindingIdsDisabledByLayer,
      layerOpacities: bridged.layerOpacities,
      layerHighlights: bridged.layerHighlights,
      layerConfidenceShowcase: bridged.layerConfidenceShowcase,
      layerClipMasks,
      layerWaypointConnectionsVisible: bridged.layerWaypointConnectionsVisible,
      layerWaypointLabelsVisible: bridged.layerWaypointLabelsVisible,
      layerPointIconsVisible: bridged.layerPointIconsVisible,
      layerPointIconScales: bridged.layerPointIconScales,
      layerPointClusteringEnabled: bridged.layerPointClusteringEnabled,
      tradeNpcClassesVisible: bridged.tradeNpcClassesVisible,
      filterOnlyActiveConditionGroups: bridged.filterOnlyActiveConditionGroups !== false,
      lootConditionSelection: bridged.lootConditionSelection,
      lootConditionContext: bridged.lootConditionContext,
    },
  }).filters;
}

function normalizeBridgedUi(signals) {
  const bridged = isPlainObject(signals?._map_bridged?.ui) ? signals._map_bridged.ui : {};
  const layers = isPlainObject(signals?._map_ui?.layers) ? signals._map_ui.layers : {};
  const settings = isPlainObject(signals?._map_ui?.windowUi?.settings)
    ? signals._map_ui.windowUi.settings
    : {};
  const threeDEnabled = settings.threeDEnabled !== false;
  return normalizeStatePatch({
    ui: {
      showPoints: bridged.showPoints !== false,
      showPointIcons: bridged.showPointIcons !== false,
      threeDEnabled,
      viewMode: threeDEnabled && bridged.viewMode === "3d" ? "3d" : "2d",
      autoAdjustView: settings.autoAdjustView !== false,
      sampleFromFilteredElements: settings.sampleFromFilteredElements !== false,
      effectsEnabled: settings.effectsEnabled !== false,
      focusWavesEnabled: settings.focusWavesEnabled === true,
      pointIconScale: bridged.pointIconScale ?? FISHYMAP_POINT_ICON_SCALE_DEFAULT,
      hoverFactsVisibleByLayer: layers.hoverFactsVisibleByLayer,
    },
  }).ui;
}

export function normalizeMapActionState(raw) {
  const source = isPlainObject(raw) ? raw : {};
  const rawFocusWorldPoint = isPlainObject(source.focusWorldPoint)
    ? {
        ...source.focusWorldPoint,
        historyBehavior:
          normalizeSelectionHistoryBehavior(source.focusWorldPoint.historyBehavior) || "append",
      }
    : null;
  const focusWorldPoint = rawFocusWorldPoint
    ? normalizeStatePatch({
        commands: { selectWorldPoint: rawFocusWorldPoint },
      }).commands?.selectWorldPoint || null
    : null;
  return {
    resetViewToken: Number.isFinite(Number(source.resetViewToken))
      ? Number(source.resetViewToken)
      : 0,
    resetUiToken: Number.isFinite(Number(source.resetUiToken))
      ? Number(source.resetUiToken)
      : 0,
    focusSearchToken: Number.isFinite(Number(source.focusSearchToken))
      ? Number(source.focusSearchToken)
      : 0,
    focusWorldPointToken: Number.isFinite(Number(source.focusWorldPointToken))
      ? Number(source.focusWorldPointToken)
      : 0,
    focusWorldPoint,
  };
}

export function buildBridgeInputPatchFromSignals(
  signals,
  { contestSelection = null, titleProjection = null, nowSeconds = Date.now() / 1000 } = {},
) {
  const filters = normalizeBridgedFilters(signals, resolveFishingContestExpression(
    resolveTitleExpression(signals?._map_ui?.search?.expression, titleProjection,
      signals?._map_runtime?.catalog?.items),
    contestSelection,
    nowSeconds,
  ));
  const ui = normalizeBridgedUi(signals);
  const layerSearchEffects = buildLayerSearchEffects(filters);
  const bookmarks =
    normalizeStatePatch({
      ui: { bookmarks: signals?._map_bookmarks?.entries },
    }).ui?.bookmarks || [];
  const bookmarkSelectedIds = normalizeBookmarkSelectedIds(
    bookmarks,
    signals?._map_ui?.bookmarks?.selectedIds,
  );

  return normalizeStatePatch({
    filters: {
      ...filters,
      layerClipMasks: layerSearchEffects.effectiveLayerClipMasks,
    },
    ui: {
      ...ui,
      sharedFishState: {
        caughtIds: signals?._shared_fish?.caughtIds,
        favouriteIds: signals?._shared_fish?.favouriteIds,
        showDried: signals?._shared_fish?.showDried === true,
      },
      bookmarkSelectedIds,
      bookmarks,
    },
  });
}

export function buildBridgeCommandPatchFromSignals(signals, previousActionState = {}) {
  const current = normalizeMapActionState(signals?._map_actions);
  const previous = normalizeMapActionState(previousActionState);
  const commands = {};
  if (current.resetViewToken > previous.resetViewToken) {
    commands.resetView = true;
  }
  if (current.focusSearchToken > previous.focusSearchToken) {
    commands.focusSearch = true;
  }
  if (
    current.focusWorldPointToken > previous.focusWorldPointToken &&
    current.focusWorldPoint
  ) {
    commands.selectWorldPoint = current.focusWorldPoint;
  }
  const patch = normalizeStatePatch({ commands });
  return patch.commands ? patch : null;
}

export function sessionSnapshotToBridgePatch(snapshot) {
  const source = isPlainObject(snapshot) ? snapshot : {};
  const selection = isPlainObject(source.selection) ? source.selection : {};
  const selectionTarget = isPlainObject(selection.detailsTarget)
    ? selection.detailsTarget
    : selection;
  const commands = {
    selectWorldPoint: {
      elementKind: selectionTarget.elementKind,
      characterIdentityKey: selectionTarget.characterIdentityKey,
      worldX: selectionTarget.worldX,
      worldZ: selectionTarget.worldZ,
      pointKind: selectionTarget.pointKind,
      pointLabel: selectionTarget.pointLabel,
      historyBehavior: selectionTarget.historyBehavior,
    },
  };
  const restoreView = normalizeRestoreView(source.view);
  if (restoreView) {
    commands.restoreView = restoreView;
    commands.setViewMode = restoreView.viewMode;
  }
  return normalizeStatePatch({ commands });
}

export function projectRuntimeSnapshotToSignals(snapshot, { catalogRevision = 0 } = {}) {
  const current = isPlainObject(snapshot) ? snapshot : createEmptySnapshot();
  return {
    _map_runtime: {
      ready: current.ready === true,
      catalogRevision: normalizeCatalogRevision(catalogRevision),
      ui: {
        bookmarks: cloneJson(current.ui?.bookmarks || []),
      },
      view: cloneJson(current.view || {}),
      selection: cloneJson(current.selection || {}),
      catalog: cloneJson(current.catalog || {}),
      statuses: cloneJson(current.statuses || {}),
    },
  };
}

export function projectSessionSnapshotToSignals(snapshot) {
  const current = isPlainObject(snapshot) ? snapshot : createEmptySnapshot();
  return {
    _map_session: {
      view: cloneJson(current.view || {}),
      selection: cloneJson(current.selection || {}),
    },
  };
}
